import { useCallback } from "react";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";

const ORDER_ENGINE_BASE =
  process.env.REACT_APP_ORDER_ENGINE_URL ||
  "https://los-workflows.bluebay-5ecdbe39.eastus2.azurecontainerapps.io";

const ROUTE_PREFIX = "loan-app-import";

export const useAppOrdersEngineRoute = () => {
  const {
    axiosPostIsLoading,
    axiosGetIsLoading,
    axiosGetRequest,
    axiosPostRequest,
    axiosSSEIsLoading,
    axiosSSEProgress,
    axiosError,
    axiosSSERequest,
  } = useAxiosHook();

  const loading = axiosGetIsLoading || axiosPostIsLoading || axiosSSEIsLoading;
  const error = axiosError;

  // ────────────────────────────────────────────────────────────
  // Steps 1-3: Upload PDF → SSE progress → Fetch result
  // ────────────────────────────────────────────────────────────
  const startLoanAppImport = useCallback(
    async ({
      file,
      userEmail,
      pipeline = "Underwriting",
      bearerToken, // optional override for testing
      onProgressFn,
      onProcessCompleteFn,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    }) => {
      validateRequiredFields({ userEmail, pipeline });

      // Build FormData manually to match backend expectations:
      //   Part 1: "submission" → JSON string with user_email + pipeline
      //   Part 2: "uploadFiles" → the PDF file binary
      const fd = new FormData();
      fd.append(
        "submission",
        JSON.stringify({ user_email: userEmail, pipeline }),
      );
      fd.append("uploadFiles", file, file.name);

      await axiosSSERequest({
        name: "Import Loan Application",
        domain: "orderEngine",
        path: `${ROUTE_PREFIX}/start`,
        // Don't use formData: true — we're passing pre-built FormData as payload
        formData: false,
        payload: fd,
        bearerToken,
        onProcessCompleteFn,
        headers: { "Content-Type": "multipart/form-data" },

        // Step 2: SSE stream URL from POST response
        getSSEUrl: (res) => res.sse_url,

        // Step 3: After SSE completes, fetch full result from /result/{instance_id}
        getStatusUrl: (res) => `${ROUTE_PREFIX}/result/${res.instance_id}`,

        onProgressFn,

        // onSuccessFn receives (resultData from GET /result, initialPostResponse)
        onSuccessFn: (resultData, initialPostResponse) => {
          // The result field may be a JSON string (Dapr serialization)
          const rawResult = resultData?.result;
          const result =
            typeof rawResult === "string" ? JSON.parse(rawResult) : rawResult;

          if (onSuccessFn) onSuccessFn(result, initialPostResponse);
        },
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosSSERequest],
  );

  // ────────────────────────────────────────────────────────────
  // Step 4: Create the deal in the LOS
  // POST /underwriting/create_loan_from_ocr_app
  // This hits the LOS backend (default domain), not the order engine
  // ────────────────────────────────────────────────────────────
  const createLoanFromImport = useCallback(
    async ({
      dealOid,
      extractedLoanData,
      documents,
      blobFileKeys,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    }) => {
      validateRequiredFields({ dealOid, extractedLoanData });

      console.log("SUBMITT", {
        deal_oid: dealOid,
        extracted_loan_data: extractedLoanData,
        documents: documents || [],
        blob_file_keys: blobFileKeys || [],
      });
      // return;
      await axiosPostRequest({
        name: "Create Loan from OCR Import",
        domain: "default", // LOS backend, uses standard Stytch auth
        path: "underwriting/create_loan_from_ocr_app",
        payload: {
          dealId: dealOid,
          // deal_oid: dealOid,
          extracted_loan_data: extractedLoanData,
          documents: documents || [],
          blob_file_keys: blobFileKeys || [],
        },

        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  // ────────────────────────────────────────────────────────────
  // Fallback: Poll /result if SSE disconnects
  // ────────────────────────────────────────────────────────────
  const pollImportResult = useCallback(
    async ({ instanceId, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ instanceId });

      await axiosGetRequest({
        name: "Poll Loan App Import Result",
        type: "orderEngine",
        path: `${ROUTE_PREFIX}/result/${instanceId}`,
        onSuccessFn: (data) => {
          if (data?.status === "COMPLETED" && data?.result) {
            const result =
              typeof data.result === "string"
                ? JSON.parse(data.result)
                : data.result;
            if (onSuccessFn) onSuccessFn(result);
          } else if (data?.status === "FAILED") {
            if (onFailFn) onFailFn(new Error("Import failed"));
          }
          // If still RUNNING, caller can poll again
        },
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  return {
    loading,
    error,
    sseProgress: axiosSSEProgress,
    startLoanAppImport,
    createLoanFromImport,
    pollImportResult,
  };
};
